import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';

import {profileDetailsDataItemT, profileDetailsTabListTabT} from '@/lib/types/form.types';

import {ProfileDetailsDataItemActionComponent} from '@/components/profileDetailsDataItemActionComponent';
import {ProfileDetailsDataItemGenericComponent} from '@/components/profileDetailsDataItemGenericComponent';
import {ProfileDetailsDataItemVisualComponent} from '@/components/profileDetailsDataItemVisualComponent';

type ProfileDetailsTabListPropsT = {
  tabs: profileDetailsTabListTabT[];
  dataItems?: profileDetailsDataItemT[];
  className?: string;
  children?: React.ReactNode;
  lowerContent?: React.ReactNode;
};

export const ProfileDetailsTabListComponent: React.FC<ProfileDetailsTabListPropsT> = ({
  tabs,
  dataItems = [],
  className,
  children,
  lowerContent,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || tabs[0].id;
  }, [location.search, tabs]);

  const [activeTab, setActiveTab] = useState(getTabFromUrl());

  useEffect(() => {
    setActiveTab(getTabFromUrl());
  }, [getTabFromUrl, location.search]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`?tab=${tabId}`);
  };

  return (
    <div className={`${className || 'col-span-12 xl:col-span-12 xxl:col-span-8'}`}>
      {children}
      <div className={`grid grid-cols-12 gap-x-6 ${children && 'mt-4'}`}>
        <div className="col-span-12 xl:col-span-12">
          <div className="box">
            <div className="box-body !p-0">
              {tabs.length > 1 && (
                <div className="items-center justify-between border-b border-dashed !p-4 dark:border-defaultborder/10 md:flex">
                  <nav className="-mb-0.5 pb-2 sm:flex md:space-x-6 rtl:space-x-reverse">
                    {tabs.map(tab => (
                      <button
                        key={tab.id}
                        className={`flex w-full items-center justify-center rounded-md px-4 py-2 text-sm sm:w-auto ${
                          activeTab === tab.id ? 'bg-primary font-semibold text-white' : 'text-primary'
                        }`}
                        onClick={() => handleTabClick(tab.id)}
                      >
                        <i className={`${tab.icon} me-1 inline-block align-middle`}></i>
                        {tab.title}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
              <div className="!p-4">
                <div className="tab-content" id="myTabContent">
                  {tabs.map((tab: profileDetailsTabListTabT) => (
                    <div
                      key={tab.id}
                      className={`tab-pane fade ${
                        activeTab === tab.id || tabs.length === 1 ? 'show active !border-0 !p-0' : 'hidden'
                      }`}
                      role="tabpanel"
                      aria-labelledby={`${tab.id}-tab`}
                    >
                      {tab.content}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {dataItems.map((item, index) => {
          switch (item.type) {
            case 'generic':
              return (
                <ProfileDetailsDataItemGenericComponent
                  type="generic"
                  key={index}
                  title={item.title}
                  data={item.data}
                  className={item.className || 'col-span-12 xl:col-span-4'}
                  stats={item.stats}
                  action={item.action}
                  dataActions={item.dataActions}
                  filters={item.filters}
                />
              );
            case 'action':
              return (
                <ProfileDetailsDataItemActionComponent
                  key={index}
                  title={item.title}
                  data={item.data}
                  className={item.className || 'col-span-12 xl:col-span-4'}
                  onViewAll={item.onViewAll}
                  dataActions={item.dataActions}
                />
              );
            case 'visual':
              return (
                <ProfileDetailsDataItemVisualComponent
                  key={index}
                  title={item.title}
                  data={item.data}
                  className={item.className || 'col-span-12 xl:col-span-4'}
                  badgeText={item.badgeText}
                />
              );
            default:
              return null;
          }
        })}
        {lowerContent && <div className="col-span-12 xl:col-span-12">{lowerContent}</div>}
      </div>
    </div>
  );
};
